import { Injectable } from '@angular/core';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';

import { PinsDaoService } from '../../dao/pins-dao.service';
import { PinsService } from '../pins.service';
import { Pin } from 'src/app/_model/pin';
import { IMSId } from 'src/app/_model/ims-object';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { PinCreateRemoteActionBuilder } from 'src/app/_model/remote-action/remote-action-builder/pin-create-remote-action-builder/pin-create-remoteaction-builder';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { ErrorService } from 'src/app/_services/error/error.service';
import { TranslateService } from '@ngx-translate/core';
import { ExtFilesService } from 'src/app/_services/ext-files/logic/ext-files.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads Pins
 */
@Injectable()
export class PinsLogicService implements PinsService {
  public pins$ = new BehaviorSubject<Pin[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private remoteActionService: RemoteActionService,
    private pinsDaoService: PinsDaoService,
    private authenticationService: AuthenticationService,
    private servicejobsService: ServicejobsService,
    private appService: AppService,
    private credentialsService: CredentialsService,
    private extFilesService: ExtFilesService,
    private errorService: ErrorService
  ) {
    this.initialize();
  }

  // Public Functions
  public async createPin(pin: Pin): Promise<Pin> {
    let newPin = await this.pinsDaoService.createPin(pin);
    await this.createPinRemote(newPin);
    await this.loadLocalPins();
    return newPin;
  }

  public getPinById(id: IMSId): Pin {
    if (id === undefined) {
      return undefined;
    }
    return this.pins$.getValue().find((pin) => {
      if (pin.id.imsId == id.imsId && pin.id.secondaryId == id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  // Resetable
  public async reset() {
    this.pinsDaoService.reset();
  }

  public async clear() {
    await this.pinsDaoService.clear();
    this.pins$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemotePins(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalPins();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async createPinRemote(pin) {
    let prrab = new PinCreateRemoteActionBuilder(
      pin,
      this.appService,
      this.credentialsService,
      this.extFilesService,
      this.logger
    );
    try {
      let pinCreateRa = await prrab.generateRemoteAction([]);
      await this.remoteActionService.sendRemoteAction(pinCreateRa);
    } catch (e) {
      this.logger.error(
        this.constructor.name,
        'Could not generate pin:',
        e.message
      );
      let message = this.translate.instant('PAGE.PLANMARKER.ERRORS.CREATE');
      this.errorService.showWarningAlert(message, e);
    }
  }

  private async loadLocalPins() {
    try {
      this.logger.log(this.constructor.name, '- Load local Pins');
      let pins: Pin[] = await this.pinsDaoService.getPins();

      this.pins$.next(pins);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Pins: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemotePins(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(this.constructor.name, '- Load new Pins from IMS');

      try {
        let pins: Pin[] = await this.loadRemotePinBatch(syncKey, []);

        this.logger.log(
          this.constructor.name,
          'Finished loading all pins (' + pins.length + ' Total)'
        );
        this.pins$.next(pins);

        this.logger.log(this.constructor.name, 'Saving pins', pins);
        await this.pinsDaoService.savePins(pins);
        this.logger.log(this.constructor.name, 'Saved all Pins');
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Pins: ',
          error?.message
        );
        throw error;
      }
    }
  }

  private async loadRemotePinBatch(
    syncKey: string,
    pins: Pin[],
    nextPart?: String,
  ): Promise<Pin[]> {
    const sjobList = this.servicejobsService.servicejobs$
      .getValue()
      .map((sjob) => {
        return sjob.getImsId();
      })
      .join(',');
    let pinsData;
    if (nextPart) {
      pinsData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            sjoblist: sjobList,
            synckey: syncKey
          },
        })
      );
    } else {
      pinsData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncpins',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            sjoblist: sjobList,
            synckey: syncKey,
          },
        })
      );
    }

    let nextId: string;
    let nextPins: Pin[] = [];
    for (const pinData of pinsData) {
      if (pinData['#nextid']) {
        nextId = pinData['#nextid'];
      } else {
        nextPins.push(Pin.fromDto(pinData));
      }
    }

    this.logger.log(
      this.constructor.name,
      'Downloaded Batch Pins: ',
      nextPins.length,
      'Total'
    );

    await this.pinsDaoService.savePins(nextPins);

    let returnPins = pins.concat(nextPins);
    if (nextId) {
      this.logger.log(
        this.constructor.name,
        'Continue to download more pins with nextid',
        nextId
      );
      return await this.loadRemotePinBatch(syncKey, returnPins, nextId);
    }

    return returnPins;
  }

  async initializeCompleted(): Promise<void> {}
}
